import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  SlidersHorizontal,
  ChevronLeft,
  ChevronRight,
  X,
  RotateCcw,
} from "lucide-react";
import { fetchDesigns } from "../api/public.api.js";
import { Container } from "../components/layout/Container.jsx";
import { SEO } from "../lib/seo.jsx";
import { DesignCard, DesignCardSkeleton } from "../components/designs/DesignCard.jsx";
import { useUrlFilters, useUrlPage } from "../hooks/useUrlFilters.js";
import { cn } from "../lib/cn.js";

const PAGE_SIZE = 12;
const DEFAULT_FILTERS = { sort: "sortOrder:asc" };

const STOREY_OPTIONS = [
  { value: "", label: "Any storey" },
  { value: "single", label: "Single" },
  { value: "double", label: "Double" },
];

const BEDROOM_OPTIONS = [
  { value: "", label: "Any beds" },
  { value: "3", label: "3 beds" },
  { value: "4", label: "4 beds" },
  { value: "5", label: "5+ beds" },
];

const BATHROOM_OPTIONS = [
  { value: "", label: "Any baths" },
  { value: "2", label: "2 baths" },
  { value: "3", label: "3 baths" },
  { value: "4", label: "4+ baths" },
];

const SORT_OPTIONS = [
  { value: "sortOrder:asc", label: "Recommended" },
  { value: "name:asc", label: "Name A→Z" },
  { value: "areaSq:asc", label: "Area (smallest)" },
  { value: "areaSq:desc", label: "Area (largest)" },
  { value: "createdAt:desc", label: "Newest" },
];

export default function HomeDesignsPage() {
  const [filters, setFilters, resetFilters] = useUrlFilters({ defaults: DEFAULT_FILTERS });
  const [page, setPage] = useUrlPage(1);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  useEffect(() => {
    if (page !== 1) setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.storey,
    filters.bedrooms,
    filters.bathrooms,
    filters.blockWidth,
    filters.blockDepth,
    filters.areaSqMin,
    filters.areaSqMax,
    filters.sort,
  ]);

  const designsQ = useQuery({
    queryKey: ["public", "designs", { ...filters, page }],
    queryFn: () =>
      fetchDesigns({
        page,
        limit: PAGE_SIZE,
        ...Object.fromEntries(
          Object.entries(filters).filter(([, v]) => v !== "" && v !== undefined)
        ),
      }),
    staleTime: 30_000,
    keepPreviousData: true,
  });

  const designs = designsQ.data?.items ?? [];
  const meta = designsQ.data?.meta;
  const setFilter = (patch) => setFilters({ ...filters, ...patch });

  return (
    <>
      <SEO
        title="Home Designs"
        description="Browse our full range of home designs."
      />

      <Container className="py-12 lg:py-16">
        <div className="mb-10">
          <p className="eyebrow">Home designs</p>
          <h1 className="mt-3 font-display text-4xl text-text-primary lg:text-5xl">
            Home Designs
          </h1>
          <p className="mt-3 max-w-2xl text-text-secondary">
            Browse our full range of home designs.
          </p>
          <p className="mt-4 text-sm text-text-muted">
            {designsQ.isLoading
              ? "Loading designs…"
              : `${meta?.total ?? designs.length} design${meta?.total === 1 ? "" : "s"} found`}
          </p>
        </div>

        {/* Top filter bar */}
        <div className="sticky top-16 z-20 -mx-4 mb-8 border-y border-border-subtle bg-bg-base/95 px-4 py-4 backdrop-blur-md lg:top-20 lg:mx-0 lg:rounded-2xl lg:border lg:px-6 lg:py-5">
          <div className="flex flex-wrap items-center gap-3">
            <FilterDropdown
              label="Storey"
              options={STOREY_OPTIONS}
              value={filters.storey ?? ""}
              onChange={(v) => setFilter({ storey: v })}
            />
            <FilterDropdown
              label="Bedrooms"
              options={BEDROOM_OPTIONS}
              value={filters.bedrooms ?? ""}
              onChange={(v) => setFilter({ bedrooms: v })}
            />
            <FilterDropdown
              label="Bathrooms"
              options={BATHROOM_OPTIONS}
              value={filters.bathrooms ?? ""}
              onChange={(v) => setFilter({ bathrooms: v })}
            />
            <button
              type="button"
              onClick={() => setAdvancedOpen((o) => !o)}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm transition-colors",
                advancedOpen || filters.blockWidth || filters.blockDepth || filters.areaSqMin || filters.areaSqMax
                  ? "border-accent bg-accent/15 text-accent"
                  : "border-border-subtle bg-bg-elevated text-text-secondary hover:border-border hover:text-text-primary"
              )}
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />
              More filters
            </button>

            <div className="ml-auto flex items-center gap-3">
              <select
                value={filters.sort ?? "sortOrder:asc"}
                onChange={(e) => setFilter({ sort: e.target.value })}
                className="rounded-md border border-border bg-bg-elevated px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    Sort: {opt.label}
                  </option>
                ))}
              </select>
              {hasActive(filters) && (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex items-center gap-1 text-xs text-text-muted hover:text-accent"
                >
                  <RotateCcw className="h-3 w-3" /> Reset
                </button>
              )}
            </div>
          </div>

          {/* Active chips */}
          {hasActive(filters) && (
            <div className="mt-3 flex flex-wrap gap-2 border-t border-border-subtle/40 pt-3">
              {filters.storey && (
                <Chip onRemove={() => setFilter({ storey: "" })}>{filters.storey} storey</Chip>
              )}
              {filters.bedrooms && (
                <Chip onRemove={() => setFilter({ bedrooms: "" })}>{filters.bedrooms} bed</Chip>
              )}
              {filters.bathrooms && (
                <Chip onRemove={() => setFilter({ bathrooms: "" })}>{filters.bathrooms} bath</Chip>
              )}
              {filters.blockWidth && (
                <Chip onRemove={() => setFilter({ blockWidth: "" })}>≤ {filters.blockWidth}m wide</Chip>
              )}
              {filters.blockDepth && (
                <Chip onRemove={() => setFilter({ blockDepth: "" })}>≤ {filters.blockDepth}m deep</Chip>
              )}
              {filters.areaSqMin && (
                <Chip onRemove={() => setFilter({ areaSqMin: "" })}>≥ {filters.areaSqMin} sq</Chip>
              )}
              {filters.areaSqMax && (
                <Chip onRemove={() => setFilter({ areaSqMax: "" })}>≤ {filters.areaSqMax} sq</Chip>
              )}
            </div>
          )}

          {/* Advanced panel */}
          {advancedOpen && (
            <div className="mt-4 grid grid-cols-1 gap-4 border-t border-border-subtle/40 pt-4 sm:grid-cols-2 lg:grid-cols-4">
              <PillRange
                label="Block width (max)"
                value={filters.blockWidth}
                onChange={(v) => setFilter({ blockWidth: v })}
                options={[10.5, 12.5, 14, 16, 18]}
                unit="m"
              />
              <PillRange
                label="Block depth (max)"
                value={filters.blockDepth}
                onChange={(v) => setFilter({ blockDepth: v })}
                options={[24, 26, 28, 30, 32]}
                unit="m"
              />
              <NumberField
                label="Min area (sq)"
                value={filters.areaSqMin ?? ""}
                onChange={(v) => setFilter({ areaSqMin: v })}
              />
              <NumberField
                label="Max area (sq)"
                value={filters.areaSqMax ?? ""}
                onChange={(v) => setFilter({ areaSqMax: v })}
              />
            </div>
          )}
        </div>

        {/* Results — full width */}
        {designsQ.isLoading ? (
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:gap-10">
            {Array.from({ length: 8 }).map((_, i) => (
              <DesignCardSkeleton key={i} />
            ))}
          </div>
        ) : designs.length === 0 ? (
          <EmptyResults onReset={resetFilters} />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:gap-10">
              {designs.map((d) => (
                <DesignCard key={d._id ?? d.slug} design={d} />
              ))}
            </div>
            <PaginationBar meta={meta} onChange={setPage} />
          </>
        )}
      </Container>
    </>
  );
}

function FilterDropdown({ label, options, value, onChange }) {
  const active = value !== "";
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
        className={cn(
          "appearance-none rounded-full border px-4 py-2 pr-9 text-sm transition-colors focus:outline-none focus:ring-1",
          active
            ? "border-accent bg-accent/15 text-accent focus:ring-accent"
            : "border-border-subtle bg-bg-elevated text-text-secondary hover:border-border hover:text-text-primary focus:ring-accent"
        )}
      >
        {options.map((opt) => (
          <option key={opt.value || "any"} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <ChevronRight
        className={cn(
          "pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 rotate-90",
          active ? "text-accent" : "text-text-muted"
        )}
      />
    </div>
  );
}

function Chip({ children, onRemove }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-accent/40 bg-accent/15 py-1 pl-3 pr-1.5 text-xs text-accent">
      {children}
      <button
        type="button"
        onClick={onRemove}
        className="grid h-5 w-5 place-items-center rounded-full hover:bg-accent/25"
        aria-label="Remove filter"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

function PillRange({ label, value, onChange, options, unit }) {
  return (
    <div>
      <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-text-muted">
        {label}
      </p>
      <div className="flex flex-wrap gap-1.5">
        <button
          type="button"
          onClick={() => onChange("")}
          className={cn(
            "rounded-full border px-3 py-1.5 text-xs",
            !value
              ? "border-accent bg-accent/15 text-accent"
              : "border-border-subtle bg-bg-elevated-2 text-text-secondary hover:border-border"
          )}
        >
          Any
        </button>
        {options.map((o) => (
          <button
            key={o}
            type="button"
            onClick={() => onChange(o)}
            className={cn(
              "rounded-full border px-3 py-1.5 text-xs",
              String(o) === String(value ?? "")
                ? "border-accent bg-accent/15 text-accent"
                : "border-border-subtle bg-bg-elevated-2 text-text-secondary hover:border-border"
            )}
          >
            ≤ {o}
            {unit}
          </button>
        ))}
      </div>
    </div>
  );
}

function NumberField({ label, value, onChange }) {
  return (
    <div>
      <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-text-muted">
        {label}
      </p>
      <input
        type="number"
        min="0"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-border bg-bg-elevated-2 px-3 py-2 text-sm focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
      />
    </div>
  );
}

function PaginationBar({ meta, onChange }) {
  if (!meta || meta.totalPages <= 1) return null;
  return (
    <nav className="mt-12 flex items-center justify-center gap-2" aria-label="Pagination">
      <button
        type="button"
        disabled={!meta.hasPrev}
        onClick={() => onChange(meta.page - 1)}
        className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border-subtle text-text-secondary disabled:opacity-30 hover:border-accent hover:text-accent"
        aria-label="Previous page"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <span className="px-4 py-2 text-sm text-text-secondary">
        Page <span className="text-text-primary">{meta.page}</span> of {meta.totalPages}
      </span>
      <button
        type="button"
        disabled={!meta.hasNext}
        onClick={() => onChange(meta.page + 1)}
        className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border-subtle text-text-secondary disabled:opacity-30 hover:border-accent hover:text-accent"
        aria-label="Next page"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </nav>
  );
}

function EmptyResults({ onReset }) {
  return (
    <div className="rounded-2xl border border-dashed border-border-subtle bg-bg-elevated px-6 py-16 text-center">
      <h3 className="font-display text-xl text-text-primary">No designs match these filters</h3>
      <p className="mt-2 text-sm text-text-secondary">
        Try widening the block size or removing a constraint.
      </p>
      <button type="button" onClick={onReset} className="mt-6 btn-outline">
        Reset filters
      </button>
    </div>
  );
}

function hasActive(f) {
  return Boolean(
    f.storey ||
      f.bedrooms ||
      f.bathrooms ||
      f.blockWidth ||
      f.blockDepth ||
      f.areaSqMin ||
      f.areaSqMax
  );
}